/**
 * PendingQueue — IndexedDB-backed outbox for transaction creates.
 *
 * Transactions submitted by the user are first written here so that:
 *   - the UI reflects them immediately (optimistic),
 *   - they survive page reloads and offline periods,
 *   - a single processor uploads them in order whenever the network allows.
 *
 * The processor is configured once (see `configure`) and is then triggered
 * automatically on enqueue, on `online` events, on focus, and on a periodic
 * retry. Each item carries its own `localId` (the same id used for the
 * optimistic row in the React Query cache) so the processor can swap the
 * temporary row for the server-authoritative one after upload.
 */

import { get, set, del, keys, createStore } from 'idb-keyval';

const queueStore = createStore('budgetify-pending-queue', 'queue');
const KEY_PREFIX = 'pending-tx:';

// In-memory mirror of the on-disk queue. Subscribers read from this so the UI
// can be synchronous (no awaiting an IDB read on each render).
let cache = [];
let loaded = false;
let loadPromise = null;

const listeners = new Set();
let processing = false;

let uploaderRef = null;     // (payload) => Promise<serverRow>
let onSuccessRef = null;    // (item, serverRow) => void
let onFailureRef = null;    // (item, error) => void

// ── Public: id helper ─────────────────────────────────────────
export function makeLocalId() {
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// ── Subscribers ───────────────────────────────────────────────
export function subscribe(cb) {
  listeners.add(cb);
  // Fire immediately so subscribers get the current state
  try { cb(cache); } catch (_) { /* ignore */ }
  return () => listeners.delete(cb);
}

function notify() {
  for (const cb of listeners) {
    try { cb(cache); } catch (_) { /* ignore */ }
  }
}

// ── Initial load ──────────────────────────────────────────────
async function loadAll() {
  const allKeys = await keys(queueStore);
  const items = await Promise.all(
    allKeys
      .filter((k) => String(k).startsWith(KEY_PREFIX))
      .map((k) => get(k, queueStore)),
  );
  cache = items.filter(Boolean).sort((a, b) => a.createdAt - b.createdAt);
  // Reset any stuck "uploading" status from a previous tab/crash
  cache = cache.map((i) => (i.status === 'uploading' ? { ...i, status: 'pending' } : i));
  loaded = true;
  notify();
}

export function ready() {
  if (loaded) return Promise.resolve();
  if (!loadPromise) loadPromise = loadAll();
  return loadPromise;
}

// ── Mutators ──────────────────────────────────────────────────
export async function enqueue({ localId, payload, dashboardId }) {
  await ready();
  const id = localId || makeLocalId();
  const item = {
    localId: id,
    payload,
    dashboardId: dashboardId || payload?.dashboard_id || null,
    status: 'pending', // 'pending' | 'uploading' | 'failed'
    createdAt: Date.now(),
    attempts: 0,
    error: null,
  };
  await set(KEY_PREFIX + id, item, queueStore);
  cache = [...cache, item];
  notify();
  return item;
}

async function patch(localId, changes) {
  const idx = cache.findIndex((i) => i.localId === localId);
  if (idx === -1) return;
  const next = { ...cache[idx], ...changes };
  await set(KEY_PREFIX + localId, next, queueStore);
  const copy = cache.slice();
  copy[idx] = next;
  cache = copy;
  notify();
}

export async function remove(localId) {
  await del(KEY_PREFIX + localId, queueStore);
  cache = cache.filter((i) => i.localId !== localId);
  notify();
}

export function getAll() { return cache; }
export function getCount() { return cache.length; }
export function isLoaded() { return loaded; }

// ── Configure the upload pipeline ─────────────────────────────
export function configure({ uploader, onSuccess, onFailure }) {
  uploaderRef = uploader || null;
  onSuccessRef = onSuccess || null;
  onFailureRef = onFailure || null;
}

// ── Processor: upload all pending items in order ──────────────
export async function process() {
  if (!uploaderRef) return;
  if (processing) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  await ready();
  if (cache.length === 0) return;

  processing = true;
  try {
    // Snapshot to avoid races if `enqueue` runs mid-loop
    const items = cache.filter((i) => i.status !== 'uploading');
    for (const item of items) {
      // The cache may have changed (e.g. removed by another caller)
      const live = cache.find((i) => i.localId === item.localId);
      if (!live) continue;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break;

      await patch(item.localId, { status: 'uploading', error: null });
      let serverRow;
      try {
        serverRow = await uploaderRef(item.payload);
      } catch (err) {
        await patch(item.localId, {
          status: 'failed',
          error: errorMessage(err),
          attempts: (live.attempts || 0) + 1,
        });
        if (onFailureRef) {
          try { onFailureRef(live, err); } catch (_) { /* ignore */ }
        }
        // If the network just dropped, stop trying more items
        if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
        continue;
      }

      await remove(item.localId);
      if (onSuccessRef) {
        try { onSuccessRef(live, serverRow); } catch (_) { /* ignore */ }
      }
    }
  } finally {
    processing = false;
  }
}

function errorMessage(err) {
  if (!err) return 'Upload failed';
  if (err.response) {
    const data = err.response.data;
    if (data?.error) return String(data.error);
    return `HTTP ${err.response.status}`;
  }
  if (err.message) return err.message;
  return String(err);
}
